import React, { useEffect, useRef, useState } from 'react';
import { listenToRideMessages, sendRideMessage } from '../services/firebase';

export default function ChatPanel({ rideId, userId, role, t, open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open || !rideId) return undefined;
    return listenToRideMessages(rideId, setMessages);
  }, [open, rideId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  if (!open) return null;

  // One language only — follows captain app lang toggle.
  const QUICK_CHIPS = [
    { id: 'arrived', text: t('chat.chipArrived') },
    { id: 'eta', text: t('chat.chipEta') },
    { id: 'call', text: t('chat.chipCall') },
    { id: 'traffic', text: t('chat.chipTraffic') },
  ];

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await sendRideMessage(rideId, userId, text, role);
      setText('');
    } finally {
      setSending(false);
    }
  };

  const handleChipClick = async (chipText) => {
    if (!chipText || sending) return;
    setSending(true);
    try {
      await sendRideMessage(rideId, userId, chipText, role);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="chat-backdrop" role="presentation" onClick={onClose}>
      <div className="chat-panel" onClick={(e) => e.stopPropagation()}>
        <div className="chat-panel-head">
          <strong>{t('chat.title')}</strong>
          <button type="button" className="chat-close" onClick={onClose}>✕</button>
        </div>
        <div className="chat-messages">
          {messages.length === 0 && <p className="text-muted chat-empty">{t('chat.empty')}</p>}
          {messages.map((m) => (
            <div key={m.id} className={`chat-bubble${m.senderId === userId ? ' mine' : ''}`}>
              <div className="chat-bubble-text">{m.text}</div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {role === 'driver' && (
          <div className="chat-chips-row" style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '6px 0' }}>
            {QUICK_CHIPS.map((c) => (
              <button
                key={c.id}
                type="button"
                className="chat-chip"
                disabled={sending}
                style={{ fontSize: 12, padding: '6px 10px', borderRadius: 16, whiteSpace: 'nowrap' }}
                onClick={() => handleChipClick(c.text)}
              >
                {c.text}
              </button>
            ))}
          </div>
        )}

        <div className="chat-input-row">
          <input
            type="text"
            value={text}
            placeholder={t('chat.placeholder')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          />
          <button type="button" className="btn btn-green chat-send" disabled={sending || !text.trim()} onClick={send}>
            {t('chat.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
